'use client';

import {useEffect} from 'react';

function hasAnalyticsConsent() {
  if (typeof window === 'undefined') return false;
  return window.OnetrustActiveGroups?.includes('V2STACK42') ?? false;
}

export default function GoogleTagManager({gtmId}: {gtmId: string}) {
  useEffect(() => {
    const updateConsent = () => {
      if (typeof window.gtag !== 'function') return;
      const granted = hasAnalyticsConsent();
      window.gtag('consent', 'update', {
        analytics_storage: granted ? 'granted' : 'denied',
      });
    };

    // run once on mount in case OneTrust resolved before this effect attached
    updateConsent();

    const originalWrapper = window.OptanonWrapper;
    window.OptanonWrapper = function () {
      originalWrapper?.();
      updateConsent();
    };

    const handleConsentChange = () => updateConsent();
    window.addEventListener('consent.onetrust', handleConsentChange);

    return () => {
      window.OptanonWrapper = originalWrapper;
      window.removeEventListener('consent.onetrust', handleConsentChange);
    };
  }, []);

  // Inject GTM scripts imperatively (no React-rendered or dangerouslySet-HTML
  // script tags). Inline scripts created via createElement+text execute
  // synchronously on append, so the consent defaults run before the loader.
  useEffect(() => {
    if (document.getElementById('gtm-loader')) return;
    // Strip anything outside [A-Z0-9-] from the env-supplied id as a defense
    // against accidentally injecting JS-breaking characters.
    const safeId = gtmId.replace(/[^A-Z0-9-]/gi, '');
    if (!safeId) return;

    const defaults = document.createElement('script');
    defaults.id = 'gtm-consent-defaults';
    defaults.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
    `;
    document.head.appendChild(defaults);

    const loader = document.createElement('script');
    loader.id = 'gtm-loader';
    loader.text = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${safeId}');
    `;
    document.head.appendChild(loader);
  }, [gtmId]);

  return null;
}
